import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import DOMPurify from 'dompurify';
import '../styles/CookieConsent.css';
import Logo from '../website_images/Logos/primary_logo.png'

const CookieConsent: React.FC = () => {
  const { t, i18n: i18nInstance } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [statsChecked, setStatsChecked] = useState(false);
  const [mediaChecked, setMediaChecked] = useState(false);
  const [showIndividualSettings, setShowIndividualSettings] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [, forceUpdate] = useState({});

  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setIsVisible(true);
    } else {
      // Parse existing settings if they exist
      try {
        const settings = JSON.parse(cookieConsent);
        setStatsChecked(settings.statistiken || false);
        setMediaChecked(settings.externeMedien || false);
      } catch (e) {
        // Handle legacy 'accepted' format
        if (cookieConsent === 'accepted') {
          setStatsChecked(true);
          setMediaChecked(true);
        }
      }
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      essenziell: true,
      statistiken: true,
      externeMedien: true,
    }));
    setStatsChecked(true);
    setMediaChecked(true);
    setIsVisible(false);
  };

  const saveSettings = () => {
    // Save chosen preferences in localStorage
    localStorage.setItem('cookieConsent', JSON.stringify({
      essenziell: true,
      statistiken: statsChecked,
      externeMedien: mediaChecked,
    }));
    setIsVisible(false);
  };

  const acceptOnlyEssential = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      essenziell: true,
      statistiken: false,
      externeMedien: false,
    }));
    setStatsChecked(false);
    setMediaChecked(false);
    setIsVisible(false);
  };

  const openCustomSettings = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setShowIndividualSettings(true);
      setIsTransitioning(false);
    }, 250);
  };

  const goBackToMainSettings = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setShowIndividualSettings(false);
      setIsTransitioning(false);
    }, 250);
  };

  // For testing - reset consent
  const resetCookieConsent = () => {
    localStorage.removeItem('cookieConsent');
    setIsVisible(true);
  };

  // For testing - force show cookie consent in current language
  const forceShowCookieConsent = () => {
    localStorage.removeItem('cookieConsent');
    setIsVisible(true);
  };

  useEffect(() => {
    (window as any).resetCookieConsent = resetCookieConsent;
    (window as any).forceShowCookieConsent = forceShowCookieConsent;
  }, []);

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);
  
  if (!isVisible) return null;

  return (
    <div className="cookie-consent-overlay">
      <div className={`cookie-consent-modal ${isTransitioning ? (showIndividualSettings ? 'closing' : 'opening') : (showIndividualSettings ? 'opening active' : '')}`} role="dialog" aria-modal="true" aria-labelledby="cookie-title" aria-describedby="cookie-desc">
        {!showIndividualSettings ? (
          // Main cookies page
          <>
            <header className="cookie-header">
              <img src={Logo} alt="Votera Logo" className="cookie-logo" />
              <h2 id="cookie-title">{t('cookieTitle')}</h2>
            </header>

            <section className="cookie-text" id="cookie-desc">
              <p>{t('cookieMainText')}</p>
              <p>{t('cookieAgeText')}</p>
              <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t('cookieDetailedText')) }}></p>
            </section>

            <form className="cookie-options" aria-label={t('cookieTitle')}>
              <label>
                <input type="checkbox" checked disabled />
                {t('cookieEssential')}
              </label>
              <label>
                <input 
                  type="checkbox" 
                  checked={statsChecked} 
                  onChange={e => setStatsChecked(e.target.checked)} 
                />
                {t('cookieStatistics')}
              </label>
              <label>
                <input 
                  type="checkbox" 
                  checked={mediaChecked} 
                  onChange={e => setMediaChecked(e.target.checked)} 
                />
                {t('cookieExternalMedia')}
              </label>
            </form>

            <div className="cookie-buttons">
              <button className="btn grey" onClick={acceptAll}>{t('cookieAcceptAll')}</button>
              <button className="btn grey" onClick={saveSettings}>{t('cookieSave')}</button>
              <button className="btn grey" onClick={acceptOnlyEssential}>{t('cookieEssentialOnly')}</button>
              <button className="btn black" onClick={openCustomSettings}>{t('cookieIndividualSettings')}</button>
            </div>

            <nav className="footer-links" aria-label="Additional Links">
              <a href="/">{t('cookieDetails')}</a> | <a href="/datenschutz">{t('dataSecurity')}</a> | <a href="/impressum">{t('imprint')}</a>
            </nav>
          </>
        ) : (
          // Individual settings page
          <>
            <header className="cookie-header">
              <img src={Logo} alt="Votera Logo" className="cookie-logo" />
              <h2 id="cookie-title">{t('cookieIndividualTitle')}</h2>
            </header>

            <section className="cookie-text" id="cookie-desc">
              <p>{t('cookieIndividualText')}</p>
            </section>

            <div className="individual-settings-buttons">
              <button className="btn grey" onClick={acceptAll}>{t('cookieAcceptAll')}</button>
              <button className="btn grey" onClick={saveSettings}>{t('cookieSave')}</button>
              <button className="btn grey" onClick={acceptOnlyEssential}>{t('cookieEssentialOnly')}</button>
              <a href="#" className="back-link" onClick={(e) => { e.preventDefault(); goBackToMainSettings(); }}>{t('cookieBack')}</a>
            </div>

            <div className="individual-cookie-sections">
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <h3>{t('cookieEssential')} (1)</h3>
                </div>
                <p>{t('cookieEssentialDesc')}</p>
                <div className="cookie-category-footer">
                  <a href="#" className="cookie-info-link">{t('cookieShowInfo')}</a>
                </div>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header">
                  <h3>{t('cookieStatistics')} (1)</h3>
                  <div className="cookie-toggle-switch">
                    <input 
                      type="checkbox" 
                      id="stats-toggle"
                      checked={statsChecked} 
                      onChange={e => setStatsChecked(e.target.checked)} 
                    />
                    <label htmlFor="stats-toggle" className="toggle-label">
                      <span className="toggle-text">{statsChecked ? t('cookieToggleOn') : t('cookieToggleOff')}</span>
                    </label>
                  </div>
                </div>
                <p>{t('cookieStatsDesc')}</p>
                <div className="cookie-category-footer">
                  <a href="#" className="cookie-info-link">{t('cookieShowInfo')}</a>
                </div>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header">
                  <h3>{t('cookieExternalMedia')} (1)</h3>
                  <div className="cookie-toggle-switch">
                    <input 
                      type="checkbox" 
                      id="media-toggle"
                      checked={mediaChecked} 
                      onChange={e => setMediaChecked(e.target.checked)} 
                    />
                    <label htmlFor="media-toggle" className="toggle-label">
                      <span className="toggle-text">{mediaChecked ? t('cookieToggleOn') : t('cookieToggleOff')}</span>
                    </label>
                  </div>
                </div>
                <p>{t('cookieMediaDesc')}</p>
                <div className="cookie-category-footer">
                  <a href="#" className="cookie-info-link">{t('cookieShowInfo')}</a>
                </div>
              </div>
            </div>

            <nav className="footer-links" aria-label="Additional Links">
              <a href="/datenschutz">{t('dataSecurity')}</a> | <a href="/impressum">{t('imprint')}</a>
            </nav>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
